/**
 * Sidebar — liste plate des sections (icone + label).
 *
 * Lit la config `NAV_ITEMS` et affiche chaque entree comme NavLink.
 * Les entrees `enabled: false` apparaissent grisees avec leur sprint cible
 * en tooltip. Click sur une entree non-implementee mene a `<ComingSoonPage>`.
 *
 * Badges live :
 * - `drafts` → compteur pending via `useDraftsPendingCount` (poll 60s,
 *   pause arrière-plan, refetch on focus). L'invalidation est immédiate
 *   sur approve/regenerate/reject — pas d'attente du tick poll.
 *
 * Pas d'accordeon : la nav secondaire des sections riches viendra plus tard
 * (split-nav layout en S6+ si besoin).
 */
import { NavLink } from 'react-router'

import Badge from '@/components/ui/Badge'
import { NAV_ITEMS } from '@/config/navigation'
import { useDraftsPendingCount } from '@/hooks/useDrafts'
import { useQueueFailedCount } from '@/hooks/useQueue'

import HeaderUser from './HeaderUser'
import './Sidebar.css'

export default function Sidebar() {
  const pendingCountQuery = useDraftsPendingCount()
  const draftsPending = pendingCountQuery.data?.count ?? 0
  const queueFailedQuery = useQueueFailedCount()
  const queueFailed = queueFailedQuery.data?.total ?? 0

  return (
    <aside className="sidebar" aria-label="Navigation principale">
      <div className="sidebar-brand">
        <span className="sidebar-brand-eyebrow">Atelier</span>
        <span className="sidebar-brand-title">sgraphic</span>
      </div>

      <nav className="sidebar-nav">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon
          const tooltip = item.enabled
            ? item.label
            : `${item.label} — disponible en ${item.enableIn ?? 'sprint à venir'}`
          const showDraftsBadge = item.id === 'drafts' && item.enabled && draftsPending > 0
          const showQueueFailedBadge = item.id === 'queue' && item.enabled && queueFailed > 0
          return (
            <NavLink
              key={item.id}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) =>
                [
                  'sidebar-item',
                  isActive ? 'sidebar-item--active' : '',
                  item.enabled ? '' : 'sidebar-item--placeholder',
                ]
                  .filter(Boolean)
                  .join(' ')
              }
              title={
                showQueueFailedBadge
                  ? `${tooltip} — ${queueFailed} publication(s) en échec`
                  : tooltip
              }
              data-testid={`nav-${item.id}`}
            >
              <Icon size={16} className="sidebar-icon" aria-hidden="true" />
              <span className="sidebar-label">{item.label}</span>
              {showDraftsBadge && (
                <Badge variant="info" size="sm" className="sidebar-badge">
                  {draftsPending}
                </Badge>
              )}
              {showQueueFailedBadge && (
                <Badge variant="danger" size="sm" className="sidebar-badge">
                  {queueFailed}
                </Badge>
              )}
              {!item.enabled && (
                <span className="sidebar-placeholder-tag" aria-hidden="true">
                  ·
                </span>
              )}
            </NavLink>
          )
        })}
      </nav>

      <HeaderUser />
    </aside>
  )
}
